import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
  Divider,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  Tune as TuneIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';
import { settingsService, CompanySettings, UserPreferences } from '../../services/settingsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  department: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordVisibility {
  current: boolean;
  new: boolean;
  confirm: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
};

/**
 * Calculate password strength based on various criteria
 * Returns a score from 0-100
 */
const calculatePasswordStrength = (password: string): number => {
  if (!password) return 0;

  let strength = 0;

  // Length check (max 40 points)
  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 20;

  // Uppercase letters (15 points)
  if (/[A-Z]/.test(password)) strength += 15;

  // Lowercase letters (15 points)
  if (/[a-z]/.test(password)) strength += 15;

  // Numbers (15 points)
  if (/[0-9]/.test(password)) strength += 15;

  // Special characters (15 points)
  if (/[^A-Za-z0-9]/.test(password)) strength += 15;

  return Math.min(strength, 100);
};

/**
 * Get password strength color based on score
 */
const getPasswordStrengthColor = (strength: number): 'error' | 'warning' | 'info' | 'success' => {
  if (strength < 40) return 'error';
  if (strength < 60) return 'warning';
  if (strength < 80) return 'info';
  return 'success';
};

/**
 * Get password strength label based on score
 */
const getPasswordStrengthLabel = (strength: number): string => {
  if (strength === 0) return '';
  if (strength < 40) return 'Weak';
  if (strength < 60) return 'Fair';
  if (strength < 80) return 'Good';
  return 'Strong';
};

/**
 * Generate initials from full name for avatar display
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Profile form state
  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    name: '',
    email: '',
    phone: '',
    department: '',
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordVisibility, setPasswordVisibility] = useState<PasswordVisibility>({
    current: false,
    new: false,
    confirm: false,
  });

  // Company settings state
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    companyName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    gstin: '',
    bankName: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankBranch: '',
  });

  // Preferences state
  const [preferences, setPreferences] = useState<UserPreferences>({
    language: 'en',
    dateFormat: 'DD/MM/YYYY',
    currency: 'INR',
    notifications: 'true',
    theme: 'blue',
  });
  const [preferencesSaving, setPreferencesSaving] = useState(false);

  // Load initial data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setPageLoading(true);

        // Load user profile data
        if (user) {
          setProfileForm({
            name: user.name || '',
            email: user.email || '',
            phone: '', // Will be loaded from API
            department: user.department || '',
          });

          // Fetch full user profile data
          try {
            const userData = await authService.getCurrentUser();
            setProfileForm({
              name: userData.name || '',
              email: userData.email || '',
              phone: (userData as any).phone || '',
              department: userData.department || '',
            });
          } catch (error) {
            console.error('Error loading user profile:', error);
          }
        }

        // Load company settings if admin
        if ((user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN')) {
          try {
            const response = await settingsService.getCompanySettings();
            if (response.settings) {
              setCompanySettings(response.settings);
            }
          } catch (error) {
            console.error('Error loading company settings:', error);
          }
        }

        // Preferences are per-user, not admin-gated.
        try {
          const response = await settingsService.getPreferences();
          if (response.preferences) {
            setPreferences((prev) => ({ ...prev, ...response.preferences }));
          }
        } catch (error) {
          console.error('Error loading preferences:', error);
        }
      } finally {
        setPageLoading(false);
      }
    };

    loadInitialData();
  }, [user]);

  const handlePreferenceChange = (field: keyof UserPreferences) => (e: SelectChangeEvent) => {
    setPreferences((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSavePreferences = async () => {
    setPreferencesSaving(true);
    try {
      // date_format (not dateFormat) matches what getUserPreferences actually
      // reads back — see settingsController.getUserPreferences, which maps
      // the stored `date_format` key to the `dateFormat` response field.
      const { dateFormat, ...rest } = preferences;
      await settingsService.updatePreferences({ ...rest, date_format: dateFormat } as Partial<UserPreferences>);
      showSnackbar('Preferences saved', 'success');
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to save preferences', 'error');
    } finally {
      setPreferencesSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  // Profile form handlers
  const handleProfileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      await authService.updateProfile({
        name: profileForm.name,
        phone: profileForm.phone,
      });

      showSnackbar('Profile updated successfully', 'success');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      showSnackbar(
        error?.response?.data?.message || 'Failed to update profile',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Password form handlers
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field: keyof PasswordVisibility) => {
    setPasswordVisibility(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      showSnackbar('Please fill in all password fields', 'warning');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showSnackbar('New passwords do not match', 'error');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      showSnackbar('New password must be at least 8 characters long', 'error');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      showSnackbar('New password must be different from current password', 'error');
      return;
    }

    try {
      setLoading(true);

      await authService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword
      );

      // Clear form on success
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      showSnackbar('Password changed successfully', 'success');
    } catch (error: any) {
      console.error('Error changing password:', error);
      showSnackbar(
        error?.response?.data?.message || 'Failed to change password',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Company settings handlers
  const handleCompanySettingsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCompanySettings(prev => ({ ...prev, [name]: value }));
  };

  const handleCompanySettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      await settingsService.updateCompanySettings(companySettings);

      showSnackbar('Company settings updated successfully', 'success');
    } catch (error: any) {
      console.error('Error updating company settings:', error);
      showSnackbar(
        error?.response?.data?.message || 'Failed to update company settings',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Password strength calculation
  const passwordStrength = calculatePasswordStrength(passwordForm.newPassword);
  const passwordStrengthColor = getPasswordStrengthColor(passwordStrength);
  const passwordStrengthLabel = getPasswordStrengthLabel(passwordStrength);

  // Determine visible tabs based on user role
  const visibleTabs = [
    { label: 'Profile', icon: <PersonIcon />, index: 0 },
    { label: 'Change Password', icon: <LockIcon />, index: 1 },
  ];

  if ((user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN')) {
    visibleTabs.push({ label: 'Company Information', icon: <BusinessIcon />, index: 2 });
  }
  visibleTabs.push({ label: 'Preferences', icon: <TuneIcon />, index: 3 });

  if (pageLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        Settings
      </Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="settings tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {visibleTabs.map((tab) => (
              <Tab
                key={tab.index}
                value={tab.index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
                {...a11yProps(tab.index)}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab 1: Profile */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            <Box
              component="form"
              onSubmit={handleProfileSubmit}
              noValidate
              autoComplete="off"
            >
              {/* Avatar Section */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 4,
                }}
              >
                <Avatar
                  sx={{
                    width: 100,
                    height: 100,
                    fontSize: '2.5rem',
                    bgcolor: '#FF5722',
                    mb: 2,
                  }}
                >
                  {getInitials(profileForm.name)}
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                  {profileForm.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.role || 'User'}
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Profile Form Fields */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={profileForm.name}
                    onChange={handleProfileChange}
                    required
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={profileForm.email}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Email cannot be changed"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={profileForm.phone}
                    onChange={handleProfileChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Department"
                    name="department"
                    value={profileForm.department}
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Department is managed by administrators"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={loading}
                      sx={{ minWidth: 150 }}
                    >
                      {loading ? 'Saving...' : 'Save Profile'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Tab 2: Change Password */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Box
              component="form"
              onSubmit={handlePasswordSubmit}
              noValidate
              autoComplete="off"
            >
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ensure your password is strong and secure. Use a combination of letters,
                numbers, and special characters.
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type={passwordVisibility.current ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    disabled={loading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            onClick={() => togglePasswordVisibility('current')}
                            edge="end"
                          >
                            {passwordVisibility.current ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type={passwordVisibility.new ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    required
                    disabled={loading}
                    helperText="Minimum 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle new password visibility"
                            onClick={() => togglePasswordVisibility('new')}
                            edge="end"
                          >
                            {passwordVisibility.new ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Password Strength Indicator */}
                {passwordForm.newPassword && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Password Strength: {passwordStrengthLabel}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength}
                      color={passwordStrengthColor}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type={passwordVisibility.confirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    disabled={loading}
                    error={
                      passwordForm.confirmPassword !== '' &&
                      passwordForm.newPassword !== passwordForm.confirmPassword
                    }
                    helperText={
                      passwordForm.confirmPassword !== '' &&
                      passwordForm.newPassword !== passwordForm.confirmPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => togglePasswordVisibility('confirm')}
                            edge="end"
                          >
                            {passwordVisibility.confirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
                      disabled={loading}
                      sx={{ minWidth: 180 }}
                    >
                      {loading ? 'Changing...' : 'Change Password'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </TabPanel>

        {/* Tab 3: Company Information (Admin Only) */}
        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
          <TabPanel value={tabValue} index={2}>
            <CardContent>
              <Box
                component="form"
                onSubmit={handleCompanySettingsSubmit}
                noValidate
                autoComplete="off"
              >
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Company Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manage your company details and banking information. This information
                  will be used in invoices and reports.
                </Typography>

                <Grid container spacing={3}>
                  {/* Basic Company Information */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Basic Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="companyName"
                      value={companySettings.companyName}
                      onChange={handleCompanySettingsChange}
                      required
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="GSTIN"
                      name="gstin"
                      value={companySettings.gstin}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                      helperText="Goods and Services Tax Identification Number"
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={companySettings.address}
                      onChange={handleCompanySettingsChange}
                      multiline
                      rows={2}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={companySettings.city}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={companySettings.state}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      name="pincode"
                      value={companySettings.pincode}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={companySettings.phone}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={companySettings.email}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  {/* Bank Details Section */}
                  <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Bank Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      name="bankName"
                      value={companySettings.bankName}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      name="bankAccountNumber"
                      value={companySettings.bankAccountNumber}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      name="bankIfscCode"
                      value={companySettings.bankIfscCode}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Branch"
                      name="bankBranch"
                      value={companySettings.bankBranch}
                      onChange={handleCompanySettingsChange}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        disabled={loading}
                        sx={{ minWidth: 150 }}
                      >
                        {loading ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </TabPanel>
        )}

        {/* Tab 4: Preferences */}
        <TabPanel value={tabValue} index={3}>
          <CardContent>
            <Box sx={{ maxWidth: 480 }}>
              <Typography variant="h6" gutterBottom>
                Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                These are personal to your account and don't affect anyone else in your workspace.
              </Typography>

              <Grid container spacing={3}>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="pref-language-label">Language</InputLabel>
                    <Select
                      labelId="pref-language-label"
                      label="Language"
                      value={preferences.language}
                      onChange={handlePreferenceChange('language')}
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="hi">Hindi</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="pref-date-format-label">Date Format</InputLabel>
                    <Select
                      labelId="pref-date-format-label"
                      label="Date Format"
                      value={preferences.dateFormat}
                      onChange={handlePreferenceChange('dateFormat')}
                    >
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="pref-currency-label">Currency</InputLabel>
                    <Select
                      labelId="pref-currency-label"
                      label="Currency"
                      value={preferences.currency}
                      onChange={handlePreferenceChange('currency')}
                    >
                      <MenuItem value="INR">INR (₹)</MenuItem>
                      <MenuItem value="USD">USD ($)</MenuItem>
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.notifications === 'true'}
                        onChange={(e) =>
                          setPreferences((prev) => ({ ...prev, notifications: e.target.checked ? 'true' : 'false' }))
                        }
                      />
                    }
                    label="Email notifications"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={preferencesSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={preferencesSaving}
                  onClick={handleSavePreferences}
                  sx={{ minWidth: 150 }}
                >
                  {preferencesSaving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </TabPanel>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;
